import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  useClose,
} from '@headlessui/react';
import ChevronIcon from '@otovo/shared/components/Icons/ChevronIcon/ChevronIcon';
import useLocalizedPath from '@otovo/shared/components/Navigation/useLocalizedPath';
import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import { localeToMarket } from '@otovo/shared/lib/locale';
import { Cloud$Market } from '@otovo/shared/types/cloudEnums';
import { Otovo$Locale } from '@otovo/shared/types/otovoweb';
import {
  Sanity$DocumentLink,
  Sanity$NavbarConfig,
  Sanity$NavbarLink,
} from '@otovo/shared/types/sanityTypes';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import MenuHeading from '../FlyOutMenu/MenuHeading';
import MenuLink from '../FlyOutMenu/MenuLink';
import { getNavLinkUrl, getOptionalProps } from './navUtils';

type NavDisclosureProps = {
  heading: string;
  defaultOpen?: boolean;
  menuItems?: Array<Sanity$NavbarLink | Sanity$DocumentLink>;
  locale: Otovo$Locale;
  market: Cloud$Market;
};

const NavDisclosure = ({
  heading,
  locale,
  market,
  defaultOpen = false,
  menuItems = [],
}: NavDisclosureProps) => {
  return (
    <Disclosure as="section" defaultOpen={defaultOpen}>
      <DisclosureButton className="group flex w-full items-center justify-between rounded-md text-heading">
        <MenuHeading>{heading}</MenuHeading>
        <ChevronIcon className="h-4 w-4 rotate-90 transition-transform duration-200 group-data-[open]:-rotate-90" />
      </DisclosureButton>
      <DisclosurePanel
        transition
        className="origin-top space-y-1 transition duration-200 ease-out data-closed:-translate-y-6 data-closed:opacity-0"
      >
        <ul className="pl-4">
          {menuItems?.map((menuItem) => (
            <li key={`mobile-${menuItem._key}`}>
              <MenuLink
                href={getNavLinkUrl(menuItem, market, locale)}
                {...getOptionalProps(menuItem)}
              >
                {menuItem.label}
              </MenuLink>
            </li>
          ))}
        </ul>
      </DisclosurePanel>
    </Disclosure>
  );
};

type Props = {
  config: Sanity$NavbarConfig;
};

const MobileNavigation = ({ config }: Props) => {
  const closeMenu = useClose();

  const router = useRouter();
  const locale = router.locale as Otovo$Locale;
  const market = localeToMarket(locale);
  const image = config.mainSection.image;

  useEffect(() => {
    const handleRouteChange = () => {
      closeMenu();
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [closeMenu, router.events]);

  // Get the first and second main sections from the config
  // The second section is optional, so we handle it accordingly
  const firstMainSection = config.mainSection.sections[0];
  const secondMainSection = config.mainSection.sections[1] || null;
  const localizedAboutUsPath = useLocalizedPath('/about');

  return (
    <div className="space-y-4 px-4 pb-4">
      {/* Solutions */}
      <section>
        <MenuHeading>
          {config.solutionsSection.packagePageSection.heading}
        </MenuHeading>
        <ul>
          {config.solutionsSection.packagePageSection.pages?.map((menuItem) => (
            <li key={`mobile-${menuItem._key}`}>
              <MenuLink
                size="big"
                href={getNavLinkUrl(menuItem, market, locale)}
                {...getOptionalProps(menuItem)}
              >
                {menuItem.label}
              </MenuLink>
            </li>
          ))}
        </ul>
      </section>

      {/* Main hardware section */}
      <NavDisclosure
        heading={config.solutionsSection.productPageSection.heading}
        menuItems={config.solutionsSection.productPageSection.pages}
        locale={locale}
        market={market}
      />

      {/* The "Top level links" section is shown here */}
      <section>
        <ul>
          <li key="mobile-pricing">
            <MenuLink size="big" href="/pricing">
              {config.topLevelLinkLabels.pricing}
            </MenuLink>
          </li>
          <li key="mobile-about">
            <MenuLink size="big" href={`${localizedAboutUsPath}/`}>
              {config.topLevelLinkLabels.aboutUs}
            </MenuLink>
          </li>
        </ul>
      </section>

      <div className="border-gray-20 border-t" />
      {/* The "Other" section is shown here */}

      <NavDisclosure
        heading={firstMainSection.heading}
        defaultOpen={true}
        menuItems={firstMainSection.pages}
        locale={locale}
        market={market}
      />

      {/* Second main section */}
      {secondMainSection && (
        <NavDisclosure
          heading={secondMainSection.heading}
          menuItems={secondMainSection.pages}
          locale={locale}
          market={market}
        />
      )}
      <div className="border-gray-20 border-t" />
      <div className="overflow-hidden px-4">
        <SanityImage
          image={image}
          className="aspect-video h-full w-5/6 rounded-md object-cover"
          placeholder="blur"
          blurDataURL={image.asset.metadata.lqip}
          sizes="(max-width: 767px) 60vw, 350px"
          maxHeightFull
        />
      </div>
    </div>
  );
};

export default MobileNavigation;
